<template>
  <div class="my-signatures" data-locator="signed-documents">
    <SignedDocumentsHeader v-model:selected-signed-documents="selectedSignedDocuments"
                           v-model:display="display"
                           :no-data="noData"
                           :no-results="noResults"
                           :disabled="noData || noResults || loading"
                           :areAllSelectedInCurrentPage="areAllSelectedInCurrentPage"
                           :areSomeSelectedInCurrentPage="areSomeSelectedInCurrentPage"
                           @selectedSignature="selectedSignedDocuments = $event"
                           @toggle-all-selection="toggleGlobalSelection"
    />
    <template v-if="loading && filteredSignedDocuments.length === 0 || !fetched">
      <v-row>
        <v-col cols="12" md="4" lg="3">
          <v-skeleton-loader type="article, actions" />
        </v-col>
      </v-row>
    </template>
    <template v-else-if="noData">
      <app-empty-state
        class="mt-16"
        module="signed-documents"
        size="sm"
        :translation-key-prefix="TRANSLATION_KEY_PREFIX"
      />
    </template>
    <template v-else-if="noResults">
      <v-row class="mt-16">
        <v-col>
          <div class="d-flex flex-column justify-center align-center">
            <img src="@/assets/img/projet_signature.svg" alt="" width="300">
            <div class="my-5 text-center" style="width: 300px">
              {{$t(`${TRANSLATION_KEY_PREFIX}.noResults`)}}
            </div>
            <AppButton color="primary" @click="deleteSearchAndFilters">
              {{ $t(`${TRANSLATION_KEY_PREFIX}.noResultsButton`) }}
            </AppButton>
          </div>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <template v-if="display === DISPLAY.grid">
        <v-row class="mt-3">
          <v-col v-for="signedDocument in paginatedDocuments"
                 :key="signedDocument.id"
                 cols="12" md="3" lg="3"
          >
            <SignedDocumentsItemGrid :signedDocument="signedDocument" />
          </v-col>
        </v-row>
      </template>
      <template v-if="display === DISPLAY.table">
        <v-row>
          <v-col>
            <SignedDocumentsTable
              :signedDocuments="filteredSignedDocuments"
              :currentPage="currentPage"
              :itemsPerPage="itemsPerPage"
              :areAllSelectedInCurrentPage="areAllSelectedInCurrentPage"
              :areSomeSelectedInCurrentPage="areSomeSelectedInCurrentPage"
              @toggle-all-selection="toggleGlobalSelection"
            />
          </v-col>
        </v-row>
      </template>
    </template>

    <DeleteSignedDocumentsDialog v-if="deleteOriginals.isOpen"
                                 :documents="deleteOriginals.documents"
                                 @close="closeDeleteOriginalsDialog"
                                 @delete-success="selectedSignedDocuments = []"
    />

    <SharePane v-model:model-value="shareOriginals.isOpen"
               v-model:documents="shareOriginals.documents"
               @removeDocumentToShare="removeDocumentToShare"
    />

    <SignedDocumentsSignatoriesListDialog v-if="signatoriesListModalSignedDocument"
                                          :mnemo="roomMnemo"
                                          :signed-document="signatoriesListModalSignedDocument"
                                          @close="signatoriesListModalSignedDocument = null"
    />

    <IManageFolderPickerDialog v-if="exportToIManage.isOpen"
                               @close="closeExportToIManageDialog"
                               @folders="prepareExportSignedDocumentsToImanage"
    />

    <PolyOfficeDialog v-if="polyOfficeEnabled && dialogIsOpen"
                      @callback="polyOfficeDialogCallback"
    />
    <div v-if="displayPagination" class="d-flex justify-end ma-4">
      <AppPagination
        v-model:current-page="currentPage"
        v-model:page-size="itemsPerPage"
        :total="filteredSignedDocuments.length"
        :page-sizes="[25, 50, 100]"
      />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { mapState, mapActions, mapGetters, useStore } from 'vuex'

import AppButton from '@/common/buttons/AppButton.vue'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import { toast } from '@/common/utils/toast'
import { DISPLAY } from '@/projects/display'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { EXPORT_SIGNED_DOCUMENT_TO_IMANAGE, GET_SIGNED_DOCUMENTS } from '@/store/modules/signed-documents/action_types'

import DeleteSignedDocumentsDialog from './dialogs/DeleteSignedDocumentsDialog.vue'
import SharePane from './pane/SharePane.vue'
import SignedDocumentsHeader from './SignedDocumentsHeader.vue'
import SignedDocumentsItemGrid from './SignedDocumentsItemGrid.vue'
import SignedDocumentsSignatoriesListDialog from './SignedDocumentsSignatoriesListDialog.vue'
import SignedDocumentsTable from './SignedDocumentsTable.vue'

const TRANSLATION_KEY_PREFIX = 'project.signed-documents.SignedDocuments'

export default {
  name: 'SignedDocuments',
  components: {
    AppButton,
    AppPagination,
    DeleteSignedDocumentsDialog,
    IManageFolderPickerDialog,
    SignedDocumentsHeader,
    PolyOfficeDialog,
    SharePane,
    SignedDocumentsItemGrid,
    SignedDocumentsSignatoriesListDialog,
    SignedDocumentsTable,
  },
  provide () {
    return {
      selectedSignedDocuments: computed(() => this.selectedSignedDocuments),
      isSignedDocumentSelected: (signedDocument) => {
        return !!this.selectedSignedDocuments.find(document => document.id === signedDocument.id)
      },
      toggleSignedDocumentSelection: (signedDocument) => {
        const wasDocSelected = this.selectedSignedDocuments.some(doc => doc.id === signedDocument.id)
        if (wasDocSelected) {
          this.selectedSignedDocuments = this.selectedSignedDocuments.filter(doc => doc.id !== signedDocument.id)
        } else {
          this.selectedSignedDocuments = [...this.selectedSignedDocuments, { ...signedDocument }]
        }
      },
      unselectAllSignedDocuments: () => {
        this.selectedSignedDocuments = []
      },
      openSignatoriesListDialog: (document) => {
        this.signatoriesListModalSignedDocument = document
      },
      openDeleteOriginalsDialog: (documents) => {
        this.deleteOriginals = {
          isOpen: true,
          documents: documents,
        }
      },
      openExportToIManageDialog: (document) => {
        this.exportToIManage = {
          isOpen: true,
          document: document,
        }
      },
      openShareOriginalsPane: this.onShareOriginal,
    }
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'signed-documents-items-per-page',
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      DISPLAY,
      TRANSLATION_KEY_PREFIX,
      fetched: false,
      display: DISPLAY.grid,
      selectedSignedDocuments: [],
      deleteOriginals: {
        documents: [],
        isOpen: false,
      },
      shareOriginals: {
        documents: [],
        isOpen: false,
      },
      exportToIManage: {
        document: null,
        isOpen: false,
      },
      signatoriesListModalSignedDocument: null,
    }
  },
  head () {
    return {
      title: this.$t(`${TRANSLATION_KEY_PREFIX}.metaTitle`),
    }
  },
  computed: {
    ...mapState('signedDocuments', ['loading']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('groups', ['groups']),
    ...mapGetters('signedDocuments', ['filteredSignedDocuments', 'signedDocumentsToDisplay']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    noData () {
      return this.signedDocumentsToDisplay.length === 0 && this.fetched
    },
    noResults () {
      return this.filteredSignedDocuments.length === 0 && this.signedDocumentsToDisplay.length > 0
    },
    paginatedDocuments () {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredSignedDocuments.slice(start, end)
    },
    displayPagination () {
      return !this.loading && !this.noData && !this.noResults
    },
    areAllSelectedInCurrentPage () {
      return this.paginatedDocuments.length > 0 &&
         this.paginatedDocuments.every(doc =>
           this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id),
         )
    },
    areSomeSelectedInCurrentPage () {
      return this.paginatedDocuments.some(doc =>
        this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id),
      ) && !this.areAllSelectedInCurrentPage;
    },
 },
  async mounted () {
    await this.GET_SIGNED_DOCUMENTS({ mnemo: this.roomMnemo, params: {
      fileDetails: true,
      rightsDetails: true,
      signatureDetails: true,
    } })
    this.fetched = true
    this.GET_GROUPS(this.roomMnemo)
  },
  methods: {
    ...mapActions('signedDocuments', [GET_SIGNED_DOCUMENTS, 'setSignDateModel', 'setFiltersStartDate', 'setFiltersEndDate', 'setFiltersSignatories', 'setSearchQuery', EXPORT_SIGNED_DOCUMENT_TO_IMANAGE]),
    ...mapActions('documentSharePicker', [DocumentSharePickerActionTypes.DISABLE_MEMBERS]),
    ...mapActions('groups', ['GET_GROUPS']),
    onShareOriginal (documents) {
      if (documents.length === 1 && documents[0].rights) {
        if (documents[0].rights.readSigned.groups.length > 0) {
          const membersToDisable = this.groups.filter(group => group.id === documents[0].id)
            .flatMap(group => group.members)

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }

        if (documents[0].rights.readSigned.users.length > 0) {
          const membersToDisable = this.groups.flatMap(group => group.members)
            .filter(member => documents[0].rights.readSigned.users.find(signer => signer === member.id))

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }
      }

      this.shareOriginals = {
        isOpen: true,
        documents: documents,
      }
    },
    deleteSearchAndFilters () {
      this.setFiltersSignatories([])
      this.setFiltersStartDate(null)
      this.setFiltersEndDate(null)
      this.setSignDateModel([])
      this.setSearchQuery('')
    },
    removeDocumentToShare (document) {
      this.shareOriginals.documents.splice(
        this.shareOriginals.documents.findIndex(o => o.id === document.id),
        1,
      )

      if (this.shareOriginals.documents.length === 0) {
        this.shareOriginals.isOpen = false
      }
    },
    async prepareExportSignedDocumentsToImanage (target) {
      try {
        await this.EXPORT_SIGNED_DOCUMENT_TO_IMANAGE({
          mnemo: this.roomMnemo,
          envelopeId: this.exportToIManage.document,
          data: {
            targetId: target.selected,
          },
        })
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentToImanageSuccess`))
      } catch {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.exportSignedDocumentToImanageError`))
      }
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        toast.success(this.dialogSuccess)
      }
    },
    closeDeleteOriginalsDialog () {
      this.deleteOriginals = {
        isOpen: false,
        documents: [],
      }
    },
    closeExportToIManageDialog () {
      this.exportToIManage = {
        isOpen: false,
        document: null,
      }
    },
    selectAllInCurrentPage () {
        const newSelectedDocuments = this.paginatedDocuments?.filter(doc =>
        !this.selectedSignedDocuments.some(selectedDoc => selectedDoc.id === doc.id),
      )
      this.selectedSignedDocuments = [
        ...this.selectedSignedDocuments,
        ...newSelectedDocuments,
      ]
    },
    unselectAllInCurrentPage () {
      this.selectedSignedDocuments = this.selectedSignedDocuments.filter(selectedDoc =>
        !this.paginatedDocuments?.some(doc => doc.id === selectedDoc.id),
      )
    },
    toggleGlobalSelection () {
      if (!this.areAllSelectedInCurrentPage) {
        this.selectAllInCurrentPage()
      } else {
        this.unselectAllInCurrentPage()
      }
    },
  },
}
</script>

<style scoped>
/* transition list */
.list-complete-item {
  transition: all 0.5s;
}
.list-complete-enter,
.list-complete-leave-to {
  opacity: 0;
  transform: translateY(-50px);
}
.list-complete-leave-active {
  position: absolute;
  z-index: -1;
}
</style>
